import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ConfirmationModalProps {
  visible: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning';
}

export function ConfirmationModal({ 
  visible, 
  title, 
  message, 
  confirmText = 'Ya, Lanjutkan',
  cancelText = 'Batal',
  onConfirm, 
  onCancel,
  type = 'warning'
}: ConfirmationModalProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (visible) {
      setTimeout(() => setShow(true), 10);
    } else {
      setShow(false);
    }
  }, [visible]);

  if (!visible) return null;

  const isDanger = type === 'danger';
  const iconColor = isDanger ? 'text-red-500' : 'text-yellow-500';
  const bgColor = isDanger ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20';
  const borderColor = isDanger ? 'border-red-200 dark:border-red-500/30' : 'border-yellow-200 dark:border-yellow-500/30';
  const confirmBtnColor = isDanger 
    ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30' 
    : 'bg-yellow-500 hover:bg-yellow-600 shadow-lg shadow-yellow-500/30';

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        show ? 'bg-black/40 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={onCancel}
    >
      <div
        className={`bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-300 ${
          show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon Section */}
        <div className={`${bgColor} border-b ${borderColor} py-6 flex justify-center`}>
          <div className={`relative ${show ? 'animate-bounce-once' : ''}`}>
            <AlertTriangle className={`h-20 w-20 ${iconColor}`} strokeWidth={1.5} />
            {show && (
              <div className="absolute inset-0 animate-ping">
                <AlertTriangle className={`h-20 w-20 ${iconColor} opacity-50`} strokeWidth={1.5} />
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 space-y-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
            {title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-center whitespace-pre-line">
            {message}
          </p>
        </div>

        {/* Buttons Section */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 rounded-lg font-medium transition-all transform active:scale-95
              bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onCancel(); // Close modal after confirm
            }}
            className={`flex-1 py-3 px-4 rounded-lg font-medium text-white transition-all transform active:scale-95 ${confirmBtnColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
